import { Injectable } from '@angular/core';
import { first, switchMap, throttleTime } from 'rxjs/operators';
import { PdfModalComponent } from '../../modals/pdf-modal/pdf-modal.component';
import { Subject } from 'rxjs';
import { PayableTableObject } from '../models/payable-table-object';
import { ConfigService } from '@aiwyn/config';
import { CloudStorageApiService } from '@aiwyn/api';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root'
})
export class InvoicePdfService {

  previewInvoice$ = new Subject<number>();

  // Stores URL for pdf File.  Url is used to access google cloud storage via the CloudStorageApiService.
  pdfFileURL: string;

  constructor(
    public dialog: MatDialog,
    private configService: ConfigService,
    private cloudStorageApiService: CloudStorageApiService,
  ) {
    this.subscribeToPreviewInvoiceEvents();
  }

  /**
   * Preview PDF selected from single or multi invoice views.
   */
  previewInvoiceAsPDF(invoiceId: number): void {
    this.previewInvoice$.next(invoiceId);
  }

  /**
   * Listen for preview invoice events and make a call to retrieve the invoice from GCP by invoice id.
   */
  subscribeToPreviewInvoiceEvents(): void {
    // Prevent user from opening multiple pdf modals by double clicking on pdf image.
    this.previewInvoice$.pipe(
      throttleTime(2000),
      switchMap((invoiceId: number) => this.cloudStorageApiService.getInvoiceById(invoiceId))
    ).subscribe(pdf => {
      let file = new Blob([pdf], {type: 'application/pdf'});
      this.pdfFileURL = URL.createObjectURL(file);
      // Check if configOption.openPdfInNewWindow = true
      if (this.configService.openPdfInNewWindow) {
        window.open(this.pdfFileURL, "", "height=650,width=750");
      } else {
        this.openPdfModal(this.pdfFileURL);
      }
    });
  }

  /**
   * Open PDF modal. Default option for viewing PDF.
   */
  openPdfModal(fileUrl: string): void {
    this.dialog.open(PdfModalComponent, {
      width: '650px',
      data: fileUrl
    });
  }

  /**
   * Export single PDF file.
   */
  exportInvoiceAsPDF(invoiceId: number, invoiceNumber: string): void {
    this.cloudStorageApiService.getInvoiceById(invoiceId).pipe(first()).subscribe((pdf: BlobPart) => {
      const blob = new Blob([pdf], {type: 'application/pdf'});
      const fileName = 'invoice#' + invoiceNumber;
      // IE 11 functionality has been removed from Angular 12+
      if ((navigator as any).msSaveBlob) {
        // IE 10+
        (navigator as any).msSaveBlob(blob, fileName);
      } else {
        const link = document.createElement('a');
        // Browsers that support HTML5 download attribute
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', fileName);
          let event: any = window.document.createEvent('MouseEvents');
          document.body.appendChild(link);
          event.initMouseEvent('click', true, true, window, 1, 0, 0, 0, 0, false, false, false, false, 0, null);
          link.dispatchEvent(event);
          document.body.removeChild(link);
        }
      }
    });
  }

  /**
   * Export every selected PDFs at one time.
   */
  exportMultipleInvoicesAsPDFs(invoicesToExport: PayableTableObject[]): void {
    for (let tableObject of invoicesToExport) {
      if (tableObject.invoice.invoiceFileId != null) {
        const invoiceId = tableObject.invoice.arTran.id;
        const invoiceNum = tableObject.invoice.invoiceNumber;
        this.exportInvoiceAsPDF(invoiceId, invoiceNum);
      }
    }
  }
}
